/**
 * Drives the three-party pairing handshake from the page.
 *
 * The page is a relay, not a participant: it asks the backend for a signed
 * challenge, hands it to the workstation service, and returns the service's proof
 * to the backend. It holds no key material and can forge neither side.
 *
 * Steps 1, 2 and 5 of `documentation/hardware_service.md` §11.1.
 */

import { submitPairingChallenge, type FetchLike } from '../domain/labelPrinting';

/** Why pairing did not complete. */
export type PairingFailureReason =
    /** The backend would not issue a challenge. */
    | 'CHALLENGE_REFUSED'
    /** The local service refused or could not be reached. */
    | 'SERVICE_REFUSED'
    /** The backend would not accept the service's proof. */
    | 'BINDING_REFUSED';

/** Outcome of a pairing attempt. */
export type PairingOutcome =
    | { paired: true; installationId: string }
    | { paired: false; reason: PairingFailureReason; code?: string };

interface ChallengeResponse {
    data?: { challenge?: string; nonce?: string };
    code?: string;
}

/**
 * Run the handshake end to end.
 *
 * Never throws: a failure at any step is reported, because pairing runs in the
 * background of a page the operator is already using.
 */
export async function pairHardwareService(
    apiBaseUrl: string,
    authHeaders: Record<string, string>,
    cashPointId: number,
    fetchImpl: FetchLike,
): Promise<PairingOutcome> {
    let challenge: string;
    let nonce: string;

    try {
        const response = await fetch(`${apiBaseUrl}/hardware-service/pairing/challenge`, {
            method: 'POST',
            headers: { ...authHeaders, 'Content-Type': 'application/json' },
            body: JSON.stringify({ cashPointId }),
        });

        const body = (await response.json().catch(() => ({}))) as ChallengeResponse;

        if (!response.ok || !body.data?.challenge || !body.data.nonce) {
            return { paired: false, reason: 'CHALLENGE_REFUSED', code: body.code };
        }

        challenge = body.data.challenge;
        nonce = body.data.nonce;
    } catch {
        return { paired: false, reason: 'CHALLENGE_REFUSED' };
    }

    const proof = await submitPairingChallenge(fetchImpl, challenge);

    if (proof === null) {
        return { paired: false, reason: 'SERVICE_REFUSED' };
    }

    try {
        const response = await fetch(`${apiBaseUrl}/hardware-service/pairing/complete`, {
            method: 'POST',
            headers: { ...authHeaders, 'Content-Type': 'application/json' },
            body: JSON.stringify({ nonce, ...proof }),
        });

        const body = (await response.json().catch(() => ({}))) as { code?: string };

        if (!response.ok) {
            return { paired: false, reason: 'BINDING_REFUSED', code: body.code };
        }
    } catch {
        return { paired: false, reason: 'BINDING_REFUSED' };
    }

    return { paired: true, installationId: proof.installationId };
}
